import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

export default function Hero() {
  return (
    <section
      className="relative pt-32 lg:pt-40 pb-20 lg:pb-32 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="animate-fade-in-up">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-4 py-1.5 text-sm font-medium text-sky-800"
              data-testid="hero-badge"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {"For anyone wondering what's next"}
            </div>

            <h1
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-medium text-slate-900"
              data-testid="hero-heading"
            >
              The AI took my job
              <span className="block text-slate-500 font-light mt-2">
                {"So what do I do now?"}
              </span>
            </h1>

            <p
              className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
              data-testid="hero-subheading"
            >
              {"A grounded, step‑by‑step program to help you reassess your skills, find work that still values what you do best, and build a calmer plan for what's next — no hype, no panic."}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-6 py-3.5 text-base font-medium text-white shadow-md hover:bg-slate-800 hover:-translate-y-0.5 transition-all"
                data-testid="hero-assessment-button"
              >
                Start My Assessment
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-xl bg-white border border-slate-200 px-6 py-3.5 text-base font-medium text-slate-900 hover:bg-slate-50 hover:border-slate-300 hover:-translate-y-0.5 transition-all"
                data-testid="hero-how-it-works-button"
              >
                How It Works
              </a>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                Private &amp; supportive
              </div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-500" />
                Step‑by‑step
              </div>
            </div>
          </div>

          {/* Right: image with soft backdrop */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            <div
              aria-hidden="true"
              className="absolute -top-10 -right-10 h-72 w-72 rounded-full bg-sky-100/70 blur-3xl"
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-12 -left-8 h-64 w-64 rounded-full bg-emerald-100/60 blur-3xl"
            />
            <div className="relative rounded-3xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.08)] border border-white/60">
              <img
                src="https://customer-assets.emergentagent.com/job_job-loss-guide/artifacts/kjdd049o_Humanoid%20in%20the%20office.png"
                alt="A humanoid robot working at a desk — a calm reminder of why this moment matters"
                className="w-full h-[440px] lg:h-[520px] object-cover"
                data-testid="hero-image"
              />
            </div>

            <div
              className="absolute -bottom-6 left-6 right-6 sm:left-10 sm:right-auto sm:max-w-xs bg-white rounded-2xl border border-slate-200 shadow-lg p-5"
              data-testid="hero-floating-card"
            >
              <p className="text-sm text-slate-500">{"Today's first step"}</p>
              <p className="mt-1 text-base font-medium text-slate-900">
                {"A 10‑minute reflection on what you actually want next."}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
